import * as Sentry from '@sentry/browser';
import { sha256 } from 'js-sha256';
import cloneDeep from 'lodash/fp/cloneDeep';
import isEmpty from 'lodash/fp/isEmpty';
import getEnv from 'utils/getEnv';

import { USER_SIGNOUT } from 'dux/auth/actionTypes';
import { LOAD_CART_SUCCESS, UPDATE_CART_REQUEST } from 'dux/checkoutCart/actionTypes';
import { getCartGift, getIsReceivingGift, getIsSubscribeable } from 'dux/checkoutCart/selectors';
import { LOAD_ORDER_SUCCESS } from 'dux/checkoutOrder/actionTypes';
import {
  BUFFER_ANSWER,
  FINISH_CONSULTATION,
  SAVE_ANSWER_REQUEST,
} from 'dux/consultation/actionTypes';
import {
  CREATE_SUBSCRIPTION_SUCCESS,
  UPDATE_SUBSCRIPTION_SUCCESS,
} from 'dux/subscriptions/actionTypes';
import {
  TRACK_BREADCRUMB,
  TRACK_CART_VIEWED,
  TRACK_GTM_EVENT,
  TRACK_HEAP_EVENT,
  TRACK_PAYMENT_STEP,
  TRACK_PRESELECT,
  TRACK_SEGMENT_EVENT,
} from 'dux/tracking/actions';
import { getGtmPaymentDetails } from 'dux/tracking/selectors';
import { ADDRESSES_SUCCESS, CREATE_ADDRESS_SUCCESS, USER_SUCCESS } from 'dux/user/actionTypes';
import { getUserGeolocationGuessedCountry, getUserProfile } from 'dux/user/selectors';

import * as ApiClient from 'Services/APIClient';

import { countriesCode } from 'constants/countries';
import { productsCategories } from 'constants/products';
import { formatPhone } from 'utils/textUtils';
import { getAnswersData } from '../consultation/selectors';

// wraps the tracker functions to avoid errors if they are not defined
const gtm = payload => {
  if (typeof window?.dataLayer?.push === 'function') {
    // deep clone the object because some pointers are on redux and GTM mutates the payload it receives
    window.dataLayer.push(cloneDeep(payload));
  }
};

const heap = {
  addEventProperties: payload =>
    window.heap && window.heap.addEventProperties && window.heap.addEventProperties(payload),
  identify: payload => window.heap && window.heap.identify && window.heap.identify(payload),
  resetIdentity: () => window.heap && window.heap.resetIdentity && window.heap.resetIdentity(),
  clearEventProperties: () =>
    window.heap && window.heap.clearEventProperties && window.heap.clearEventProperties(),
  track: (event, payload) => window.heap && window.heap.track && window.heap.track(event, payload),
};

const segment = {
  identify: payload => window?.analytics?.identify(payload),
  track: (event, payload) => window?.analytics?.track(event, { properties: payload }),
};

// check if we are in test environment
const testEnv = getEnv('NODE_ENV') === 'test';
const developmentEnv = getEnv('NODE_ENV') === 'development';

const HEAP_EVENT_PROPERTIES = [
  'can_see_feedback',
  'hair_profile_status',
  'has_accepted_terms',
  'has_active_subscription',
  'has_onhold_subscription',
  'has_redeemable_gift',
  'order_count',
];

const uiStepToString = {
  'contact-address': 'Account/Shipping',
  payment: 'Payment',
  confirmation: 'Review',
  success: 'Order Success',
};

const trackPaymentStep = ({ state, step }) => {
  let event = '';
  if (getIsReceivingGift(state)) {
    event += 'Giftee ';
  } else if (getCartGift(state)) {
    event += 'Gifter ';
  }
  event += uiStepToString[step];
  event += ' Viewed';
  heap.track(event);
};

export const filterHeapEventProperties = (data = {}) => {
  const filteredKeys = Object.keys(data)
    .filter(key => {
      return HEAP_EVENT_PROPERTIES.includes(key);
    })
    .reduce((obj, key) => {
      return { ...obj, [key]: data[key] };
    }, {});
  if (!isEmpty(data)) {
    // As of today this tracking function is only called for authed profile but better be safe not creating erroneous data
    filteredKeys.user_type = data.order_count > 0 ? 'customer' : 'account_holder';
    filteredKeys.subscriber_status =
      data.has_active_subscription || data.has_onhold_subscription
        ? 'subscribed'
        : data.has_canceled_subscription
        ? 'was_subscribed'
        : 'never_subscribed';
  }
  return filteredKeys;
};

const getGtmProducts = (items, catalog) => {
  if (catalog.length) {
    return items
      .filter(item => item.category === productsCategories.FORMULA)
      .map(item => {
        const catalogItem = catalog.find(i => i.type === item.type);
        return {
          name: catalogItem.label,
          item_price: catalogItem.unit_price,
          type: catalogItem.type,
          quantity: item.quantity,
          subscription: item?.subscription?.active || false,
          subscriptionFrequency: item?.subscription?.frequency,
        };
      });
  }
  return null;
};

const trackGtmCartLoaded = (action, state) => {
  const products = getGtmProducts(action.data.items, action.data.catalog);
  const answersData = getAnswersData(state);
  if (products?.length) {
    gtm({
      event: 'cart-loaded',
      ecommerce: {
        cart: {
          products,
        },
      },
      survey_hair_texture: answersData?.diag_hair_texture,
    });
  }
};

const trackGtmCartChanged = action => {
  const products = getGtmProducts(action.items, action.data.catalog);
  if (products?.length) {
    gtm({
      event: 'change-cart',
      ecommerce: {
        cart: {
          products,
        },
      },
    });
  }
};

const middleware = store => next => action => {
  // update the state first
  const result = next(action);
  const nextState = store.getState();

  try {
    switch (action.type) {
      // user signup or signin
      case USER_SUCCESS:
        gtm({
          event: 'user_logged',
          userEmail: action.user && action.user.email,
          userEmailHashed: action.user && action.user.email && sha256(action.user.email),
          userPhoneNumber: action.user && action.user.phone && formatPhone(action.user.phone),
          userFirstName: action.user && action.user.first_name,
          userLastName: action.user && action.user.last_name,
          orderCount: action.user && action.user.profile && action.user.profile.order_count,
          survey_hair_type: action.user?.profile?.diag_hair_type,
        });
        heap.identify(action.user && action.user.pubkey);
        heap.addEventProperties({
          'Logged In': 'true',
          ...filterHeapEventProperties(action.user && action.user.profile),
        });
        if (getUserGeolocationGuessedCountry(nextState) === countriesCode.US) {
          segment.identify(action?.user?.pubkey);
        }
        break;

      case BUFFER_ANSWER:
      case SAVE_ANSWER_REQUEST:
        // send answer to GTM before page redirect (answers response)
        if (action.trackingPayload) {
          gtm({
            event: 'eventGA',
            survey_name: action.trackingPayload.name,
            survey_question_topic: action.trackingPayload.category,
            survey_question_num: action.trackingPayload.index,
            survey_question_answer: action.trackingPayload.answer,
          });
        }
        break;

      case LOAD_ORDER_SUCCESS:
        // check if a payment was made, because this action is also used on checkout success page refresh
        if (action.isPayment) {
          heap.addEventProperties(filterHeapEventProperties(getUserProfile(nextState)));
          gtm(getGtmPaymentDetails(nextState));
          trackPaymentStep({ state: nextState, step: 'success' });
        }
        break;

      // custom events sent from the UI for tracking
      case TRACK_GTM_EVENT:
        gtm(action.payload);
        break;

      case CREATE_SUBSCRIPTION_SUCCESS:
      case FINISH_CONSULTATION:
      case UPDATE_SUBSCRIPTION_SUCCESS:
        heap.addEventProperties(filterHeapEventProperties(getUserProfile(nextState)));
        break;

      case TRACK_CART_VIEWED:
        if (getIsReceivingGift(nextState)) {
          heap.track('Giftee Cart Viewed');
        } else if (getIsSubscribeable(nextState)) {
          heap.track('Subscription in Cart Viewed');
        } else {
          heap.track('Cart Viewed');
        }
        break;

      case LOAD_CART_SUCCESS:
        trackGtmCartLoaded(action, nextState);
        break;

      case UPDATE_CART_REQUEST:
        if (action.items) {
          trackGtmCartChanged(action);
        }
        break;

      case TRACK_HEAP_EVENT:
        heap.track(action.event, action.payload);
        break;

      case TRACK_SEGMENT_EVENT:
        if (getUserGeolocationGuessedCountry(nextState) === countriesCode.US) {
          segment.track(action.event, action.payload);
        }
        break;

      case TRACK_PAYMENT_STEP:
        trackPaymentStep({ state: nextState, step: action.step });
        break;

      case USER_SIGNOUT:
        heap.clearEventProperties();
        heap.resetIdentity();
        heap.track('Logged Out');
        break;

      case TRACK_BREADCRUMB:
        ApiClient.post('/v1/breadcrumbs/', {
          version: 1,
          type: 'pageview',
          data: {
            url: action.pathname,
          },
        }).catch(error => {
          Sentry.withScope(scope => {
            scope.setLevel('info');
            Sentry.captureMessage(`[Breadcrumb] ${error}`);
          });
        });
        break;

      case TRACK_PRESELECT:
        ApiClient.post('/v1/breadcrumbs/', {
          version: 1,
          type: 'cta_click',
          data: {
            product: action.productSlug,
            cta_type: 'product_preselect',
            cta_location: action.locationId,
          },
        }).catch(error => {
          Sentry.withScope(scope => {
            scope.setLevel('info');
            Sentry.captureMessage(`[Breadcrumb] ${error}`);
          });
        });
        break;

      case ADDRESSES_SUCCESS:
        gtm({
          event: 'update_address',
          userAddressHashed:
            action.addresses && action.addresses[0] && sha256(action.addresses[0].address1),
          userAddressCity: action.addresses && action.addresses[0] && action.addresses[0].city,
          userAddressState: action.addresses && action.addresses[0] && action.addresses[0].state,
          userAddressZip: action.addresses && action.addresses[0] && action.addresses[0].zipcode,
        });
        break;

      case CREATE_ADDRESS_SUCCESS:
        gtm({
          event: 'create_address',
          userAddressHashed: action.address && sha256(action.address.address1),
          userAddressCity: action.address && action.address.city,
          userAddressState: action.address && action.address.state,
          userAddressZip: action.address && action.address.zipcode,
        });
        break;

      default:
        break;
    }
  } catch (error) {
    if (testEnv) {
      throw error;
    } else if (developmentEnv) {
      console.warn(error);
    } else {
      Sentry.captureException(error);
    }
  }

  return result;
};

export default middleware;
